import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ListButton } from '@app/core/components/list/list.component';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { UserGroupService, UserGroupSettingGet } from '@app/services/user-group.service';

@Component({
    selector: 'tt-usergroup-setting',
    templateUrl: './usergroup-setting.component.html',
    styleUrls: ['./usergroup-setting.component.css'],
    standalone: false
})
export class UsergroupSettingComponent implements OnInit {
    public page?: UserGroupSettingGet;

    public settings: Item[] = [];

    public itemState = '';

    public selectedId: string | number = '';

    public showEnable = false;

    public showDisable = false;

    public enabling = false;

    public disabling = false;

    public error = '';

    public readonly tablet: MediaQueryList;

    public readonly rowButtons: ListButton<Item>[] = [{ id: 'new-tab', icon: 'fas fa-window-restore', type: 'primary', onClick: ({ item }) => this.openInNewTab(item) }];

    private translations = {
        confirm_switch_off_all_message: ''
    };

    constructor(
        private readonly groups: UserGroupService,
        private readonly modal: ModalService,
        private readonly translate: TranslateService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this); this.tablet = layout.getMediaQueries().tablet;
    }

    public async ngOnInit(): Promise<void> {
        try {
            await Promise.all([
                this.loadPage(),
                this.loadItems(),
                this.translate.translateBatch(this.translations).then(values => {
                    for (const key of Object.keys(this.translations) as (keyof typeof this.translations)[]) {
                        if (values[key]) this.translations[key] = values[key];
                    }
                })
            ]);
        } catch (error) {
            this.showError(error);
        }
    }

    private get settingKey(): string | number {
        return this.navigator?.params['item_id'] as string | number ?? '';
    }

    private async loadPage(): Promise<void> {
        this.page = await this.groups.getSetting({ p2_setting_keyno: this.settingKey });
    }

    private async loadItems(): Promise<void> {
        this.settings = await this.groups.getSettings({ p2_setting_keyno: this.settingKey });

        // Legacy visibility and inline editor state are determined by the last applicable row.
        for (const item of this.settings) {
            this.showEnable = item.item_glyphicon !== 'glyphicon-check';
            this.showDisable = !this.showEnable;

            if (item.item_state && item.item_state > '') this.itemState = item.item_state;
        }
    }

    public async updateList(): Promise<void> {
        this.selectedId = '';

        try {
            await this.loadItems();
        } catch (error) {
            this.showError(error);
        }
    }

    public openItem({ item, event }: { item: Item; event: MouseEvent }): void {
        if (this.tablet.matches && this.itemState === 'usergroupsettingedit') {
            if (item.item_id !== '') this.selectedId = item.item_id;
            return;
        }

        if (item.item_state === 'customaction') {
            const action = item['item_action'];

            if (typeof action === 'function') action(item);
            return;
        }

        if (!item.item_state || item.item_path === '') return;

        const target: any = {
            to: item.item_state,
            parms: item.item_parms
        };

        if (event.ctrlKey) this.navigator?.newTab(target);
        else this.navigator?.go(target);
    }

    public openVideo(): void {
        if (this.page) window.open(this.page.video_url);
    }

    private openInNewTab(item: Item): void {
        if (item.item_state) {
            this.navigator?.newTab({
                to: item.item_state,
                parms: item.item_parms
            });
        }
    }

    public async enableAll(): Promise<void> {
        if (this.enabling) return;

        this.enabling = true;
        this.selectedId = '';

        try {
            await this.changeAll('1');
        } catch (error) {
            this.showError(error);
        } finally {
            this.enabling = false;
        }
    }
    
    public async disableAll(): Promise<void> {
        if (this.disabling) return;

        this.disabling = true;
        this.selectedId = '';

        try {
            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                message: this.translations.confirm_switch_off_all_message
            });

            if (confirmed) await this.changeAll('0');
        } catch (error) {
            this.showError(error);
        } finally {
            this.disabling = false;
        }
    }

    private async changeAll(onoff: '0' | '1'): Promise<void> {
        const response = await this.groups.changeAllSettings({ p2_setting_keyno: this.settingKey, onoff });

        if (response.errorcode !== '0') {
            await this.modal.show({
                type: 'warning',
				title: 'error',
				message: response.errormessage
            });
        } else {
            if (onoff === '1') this.enabling = false;
            else this.disabling = false;

            this.showEnable = onoff === '0';
            this.showDisable = onoff === '1';

            await this.loadItems();
        }
    }

    private showError(error: unknown): void {
        this.error = error instanceof Error ? error.message : String(error);
    }
}
